/**
 * GlobeViewer geometry/layers mixin: globe fill, dots, borders, region fills,
 * fill<->border linking, region styling, and mode coloring. Borders, fill caps,
 * and centroids are precomputed at build time (scripts/generate-region-render.ts);
 * these builders just upload the baked buffers. Methods run with `this` bound to
 * a GlobeViewer instance; composed onto the prototype in globe-viewer.ts. @ts-nocheck.
 */
/* eslint-disable */
// @ts-nocheck
import * as THREE from 'three'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { getEudiReadinessStageToken } from '../../../views/eudi-wallet'
import { matchesSelectedLegislationTags } from '../../../data/age-assurance/tags'

// EUDI wallet readiness stage color as a three.js hex int (the shared token is a
// CSS hex string, kept in sync with the map legend swatches).
function eudiReadinessStageColor(stage) {
  const token = getEudiReadinessStageToken(stage);
  return token ? parseInt(token.color.slice(1), 16) : null;
}

export const layerMethods = {
      createGlobeFill() {
        const geometry = new THREE.SphereGeometry(1.0, 64, 64);
        const material = new THREE.MeshPhongMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.9,
          shininess: 10,
        });

        const mesh = new THREE.Mesh(geometry, material);
        this.globeGroup.add(mesh);
        this.meshes.globeFill = mesh;
      }

,
      // Lazily load the Earth elevation bump map. Deferred until topography is
      // first enabled so this third-party CDN request no longer races the globe
      // data fetches during initial load. Idempotent.
      loadBumpTexture() {
        if (this.bumpTexture || this._bumpTextureLoading) return;
        this._bumpTextureLoading = true;
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(
          'https://unpkg.com/three-globe@2.31.0/example/img/earth-topology.png',
          (texture) => {
            this.bumpTexture = texture;
            this._bumpTextureLoading = false;
            // If topography is enabled, apply it now that the texture is ready.
            if (this.topographyEnabled && this.meshes.globeFill) {
              const intensitySlider = document.getElementById('topography-intensity');
              const bumpScale = intensitySlider ? parseFloat(intensitySlider.value) : 0.5;
              this.meshes.globeFill.material.bumpMap = texture;
              this.meshes.globeFill.material.bumpScale = bumpScale;
              this.meshes.globeFill.material.shininess = 40;
              this.meshes.globeFill.material.needsUpdate = true;
              this.needsRender = true;
            }
          },
          undefined,
          (error) => {
            this._bumpTextureLoading = false;
            console.warn('Failed to load bump map texture:', error);
          }
        );
      }

,
      createDotsFromPrecomputed(positions) {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

        // Custom shader material for lit dots. The field is static at its resting
        // radius (just above the surface); a top-down latitude cascade (uReveal 0->1
        // in, 1->0 out) gates each dot's appearance via vAppear.
        const vertexShader = `
          uniform float size;
          uniform vec3 lightPosition;
          uniform float uReveal;
          uniform float uEdge;
          uniform float uBaseRadius;
          varying float vLightIntensity;
          varying float vAppear;

          const float PI = 3.141592653589793;

          void main() {
            vec3 dir = normalize(position);

            // Reveal order: top-down by latitude (0 at the north pole, 1 at the south).
            float prog = 0.5 - asin(clamp(dir.y, -1.0, 1.0)) / PI;
            // Per-dot appear factor: 0 until the sweep reaches this dot's latitude.
            vAppear = smoothstep(prog, prog + uEdge, uReveal);

            vec3 displaced = dir * uBaseRadius;

            vec4 worldPosition = modelMatrix * vec4(displaced, 1.0);
            vec3 worldNormal = normalize(worldPosition.xyz);
            vec3 lightDir = normalize(lightPosition - worldPosition.xyz);
            vLightIntensity = max(dot(worldNormal, lightDir), 0.0);
            vLightIntensity = 0.3 + vLightIntensity * 0.7;

            vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
            gl_PointSize = size * vAppear;
            gl_Position = projectionMatrix * mvPosition;
          }
        `;

        // Dot gradient palette — muted, matte lavender that reads cohesively on the
        // light theme (the saturated chartsSequential cyans read too neon).
        const COLOR_DEFS = `
          const vec3 DOT_LIT    = vec3(0.902, 0.898, 1.000); // #E6E5FF soft lavender (lit side)
          const vec3 DOT_SHADOW = vec3(0.710, 0.706, 0.839); // #B5B4D6 deeper lavender (shadow side)
        `;
        const fragmentShader = `
          uniform vec3 color;
          uniform float opacity;
          ${COLOR_DEFS}
          varying float vLightIntensity;
          varying float vAppear;
          
          void main() {
            if (vAppear <= 0.001) discard;
            // Create circular point
            vec2 center = gl_PointCoord - vec2(0.5);
            float dist = length(center);
            if (dist > 0.5) discard;

            // Lighting-hue gradient: deeper lavender (shadow) -> soft lavender (lit).
            vec3 col = mix(DOT_SHADOW, DOT_LIT, vLightIntensity);

            // color uniform stays a neutral tint (settings picker); white = unchanged.
            gl_FragColor = vec4(col * color, opacity * vAppear);
          }
        `;

        const material = new THREE.ShaderMaterial({
          uniforms: {
            color: { value: new THREE.Color(0xffffff) }, // neutral tint over the gradient (settings picker)
            size: { value: 3.0 },
            opacity: { value: 1.0 }, // constant alpha; per-dot reveal handles fade in/out
            uReveal: { value: 0.0 },
            uEdge: { value: 0.2 },
            uBaseRadius: { value: 1.004 }, // resting radius, just above the globe fill (1.0)
            lightPosition: { value: new THREE.Vector3(this.keyLightX, this.keyLightY, this.keyLightZ) }
          },
          vertexShader: vertexShader,
          fragmentShader: fragmentShader,
          transparent: true,
          depthWrite: false,
        });

        const mesh = new THREE.Points(geometry, material);
        this.globeGroup.add(mesh);
        this.meshes.dots = mesh;

        // Dots start hidden; applyGlobeStyle (called right after this in phase 3)
        // decides reveal/visibility per data type — dots are exclusive to Digital IDs.
        mesh.material.uniforms.uReveal.value = 0;
        mesh.visible = false;
      }

,
      createBorders(entries, type, range) {
        const resolution = new THREE.Vector2(this.container.clientWidth, this.container.clientHeight);

        // Optional { start, count } slice so the build can be chunked across
        // frames (see loadGlobeData); defaults to the whole entry list.
        const total = entries.length;
        const start = range && range.start != null ? range.start : 0;
        const end = range && range.count != null ? Math.min(start + range.count, total) : total;

        for (let index = start; index < end; index++) {
          const entry = entries[index];
          // Baked border line positions (flat xyz pairs, already densified to
          // follow the sphere curvature — see generate-region-render.ts).
          if (!entry || !entry.border || entry.border.length === 0) continue;

          const geometry = new LineSegmentsGeometry();
          geometry.setPositions(entry.border);

          const material = new LineMaterial({
            color: this.ACTIVE_BORDER_COLOR,
            linewidth: this.DEFAULT_LINE_WIDTH,
            transparent: true,
            opacity: 1.0,
            depthTest: true,
            depthWrite: false,
            resolution: resolution,
          });

          const mesh = new LineSegments2(geometry, material);
          mesh.computeLineDistances();

          if (type === 'country') {
            const countryCode = entry.code;
            mesh.userData = {
              type: 'country',
              code: countryCode,
              name: entry.name,
              originalOpacity: 1.0,
              originalColor: this.ACTIVE_BORDER_COLOR,
              originalLineWidth: this.DEFAULT_LINE_WIDTH,
              centroid: entry.centroid
            };
            this.meshes.countryBorders.push(mesh);
            // US country border: invisible hit target for Digital IDs (we draw US from state geometry only)
            if (countryCode === 'US') {
              mesh.material.opacity = 0;
              mesh.userData.invisibleHitTarget = true;
            }
          } else {
            mesh.userData = {
              type: 'state',
              code: entry.code,
              name: entry.name,
              originalOpacity: 1.0,
              originalColor: this.ACTIVE_BORDER_COLOR,
              originalLineWidth: this.DEFAULT_LINE_WIDTH,
              centroid: entry.centroid
            };
            this.meshes.stateBorders.push(mesh);
          }

          this.globeGroup.add(mesh);
        }
      }

      // Create filled polygon meshes for hit detection and visual fills. Caps are
      // precomputed with three-conic-polygon-geometry at build time (see
      // scripts/generate-region-render.ts) so they follow the sphere curvature;
      // here we just upload the baked position/index buffers. A single mesh per
      // feature serves as both the visible fill and the raycast hit target. Each
      // baked entry is self-contained (code, name, centroid, fill, border), so
      // there is no cross-file index coupling to guard against.
,
      createRegionFills(entries, type, range) {
        // Optional { start, count } slice for chunked building (see loadGlobeData).
        const total = entries.length;
        const start = range && range.start != null ? range.start : 0;
        const end = range && range.count != null ? Math.min(start + range.count, total) : total;

        for (let index = start; index < end; index++) {
          const entry = entries[index];

          // Features with no usable fill cap (border-only territories) are kept
          // in the bundle for their borders but produce no fill mesh here.
          const geometry = this.buildFillGeometryFromBaked(entry && entry.fill);
          if (!geometry) continue;

          this.addFillMesh(entry, type, geometry);
        }
      }

      // Reconstruct a fill cap BufferGeometry from a baked fill object ({ p, i }:
      // flat position + index arrays produced by the geometry precompute).
      // Returns null for features with no usable cap (empty buffers).
,
      buildFillGeometryFromBaked(fill) {
        if (!fill || !fill.p || fill.p.length === 0 || !fill.i || fill.i.length === 0) {
          return null;
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(fill.p), 3));
        geometry.setIndex(fill.i);
        geometry.computeVertexNormals();
        return geometry;
      }

      // Wrap a prebuilt cap geometry in a fill mesh and register it from a baked
      // render entry. Keeps the userData/linking contract identical to the border
      // meshes so hover/click and fill<->border linking work uniformly.
,
      addFillMesh(entry, type, geometry) {
        if (!geometry) return;

        // Initially invisible (opacity 0): used for hit detection and turned
        // visible by applyModeColors. Caps sit at radius 1.004, comfortably above
        // the globe surface (1.0) — that gap alone avoids z-fighting, so no
        // polygonOffset is needed. depthTest stays on so the opaque globe occludes
        // far-hemisphere caps. (A negative polygonOffset was previously set here;
        // it pulled far-side caps in front of the globe in depth and caused
        // far-hemisphere bleed-through, so it was removed — see PR #52 QA.)
        const material = new THREE.MeshBasicMaterial({
          color: 0x535CFF, // Brand blue - overridden based on status
          transparent: true,
          opacity: 0.0,
          side: THREE.DoubleSide,
          depthWrite: false,
          depthTest: true,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = 0; // Render below borders

        if (type === 'country') {
          mesh.userData = {
            type: 'country',
            code: entry.code,
            name: entry.name,
            originalOpacity: 0.0,
            originalColor: 0x3b82f6,
            centroid: entry.centroid,
            borderMesh: null, // Will be linked after borders are created
          };
          this.meshes.countryFills.push(mesh);
        } else {
          mesh.userData = {
            type: 'state',
            code: entry.code,
            name: entry.name,
            originalOpacity: 0.0,
            originalColor: 0x3b82f6,
            centroid: entry.centroid,
            borderMesh: null, // Will be linked after borders are created
          };
          this.meshes.stateFills.push(mesh);
        }

        this.globeGroup.add(mesh);
      }

      // Link fill meshes to their corresponding border meshes
,
      linkFillsToBorders() {
        // Link country fills to country borders
        this.meshes.countryFills.forEach(fill => {
          const border = this.meshes.countryBorders.find(b => b.userData.code === fill.userData.code);
          if (border) {
            fill.userData.borderMesh = border;
            border.userData.fillMesh = fill;
          }
        });
        
        // Link state fills to state borders
        this.meshes.stateFills.forEach(fill => {
          const border = this.meshes.stateBorders.find(b => b.userData.code === fill.userData.code);
          if (border) {
            fill.userData.borderMesh = border;
            border.userData.fillMesh = fill;
          }
        });
        
      }

      // Single interactive-style source: all regions use this. Add country codes to COUNTRY_CODES_DRAWN_FROM_SUBREGIONS for outline + subregion fill (no coarse country fill). When jurisdiction is National, show US as a single country (outline + country fill).
,
      getRegionInteractiveStyle(mesh) {
        const isDrawnFromSubregions = mesh.userData.type === 'country' &&
          this.COUNTRY_CODES_DRAWN_FROM_SUBREGIONS.has(mesh.userData.code);
        const showAsCountry = !isDrawnFromSubregions || this.regulationLevel === 'national';
        return {
          defaultOutlineOpacity: showAsCountry ? 1 : 0,
          hoverFillOpacity: showAsCountry ? this.HOVER_FILL_OPACITY : 0,
          selectFillOpacity: showAsCountry ? this.HOVER_FILL_OPACITY : 0,
          useSubregionFills: isDrawnFromSubregions && this.regulationLevel !== 'national',
        };
      }

      // For countries drawn from subregions (e.g. US): show fill by highlighting their subregion meshes (state fills) so the fill follows state shapes.
,
      setSubregionFillsStyle(countryCode, opacity, color) {
        if (countryCode === 'US' && this.meshes.stateFills) {
          this.meshes.stateFills.forEach(fill => {
            if (fill.material) {
              if (color != null) fill.material.color.setHex(color);
              fill.material.opacity = opacity;
              fill.material.needsUpdate = true;
            }
            fill.userData.originalFillOpacity = opacity;
            fill.userData.originalFillColor = color != null ? color : 0x434343;
          });
        }
      }

,
      applyRegionDefaultStyles() {
        this.meshes.countryBorders.forEach(mesh => {
          const style = this.getRegionInteractiveStyle(mesh);
          if (style.defaultOutlineOpacity === 0) {
            mesh.material.opacity = 0;
            mesh.userData.originalOpacity = 0;
          }
        });
        this.meshes.countryFills.forEach(mesh => {
          const style = this.getRegionInteractiveStyle(mesh);
          if (style.defaultOutlineOpacity === 0) {
            mesh.material.opacity = 0;
            mesh.userData.originalOpacity = 0;
          }
        });
      }

      // Center globe on a specific country or state by code
      // regionType: 'state' | 'country' | undefined - if provided, search only that type first
      // onNearCenter: optional callback fired when rotation is near-complete
,
      applyModeColors(animate = false) {
        const dataType = this.getCurrentDataType();
        const countryClickableSet = dataType === 'digital_ids' ? this.clickableCountriesDigitalIds : this.clickableCountries;
        const statesActive = dataType === 'age_assurance';
        const selectedStatuses = window.__SELECTED_STATUSES__;

        // Build set of country codes excluded by the digital ID Level of
        // assurance / Claims filters so the globe matches the filtered table.
        // A country is excluded only when it has values for a dimension and none
        // of them are still selected (mirrors applyDigitalIdFilters).
        // EUDI Wallets tab: countries with an EUDI Wallet entry are the only
        // active ones, each painted with its readiness stage color (see the legend).
        const eudiFilterActive = dataType === 'digital_ids' && window.__EUDI_WALLET_FILTER__ === true;
        const eudiColorByCode = new Map();
        if (eudiFilterActive) {
          (window.__DIGITAL_IDS_TABLE_DATA__ || []).forEach(r => {
            if (!r.code) return;
            // Stages arrive in precedence order, so the first one wins.
            const color = eudiReadinessStageColor((r.eudiReadinessStages || [])[0]);
            if (color != null) eudiColorByCode.set(r.code.toUpperCase(), color);
          });
        }

        const digitalIdExcluded = new Set();
        if (dataType === 'digital_ids') {
          const selLoa = window.__SELECTED_LOA__;
          const selClaims = window.__SELECTED_CLAIMS__;
          if (selLoa || selClaims) {
            const digitalData = window.__DIGITAL_IDS_TABLE_DATA__ || [];
            digitalData.forEach(r => {
              if (!r.code) return;
              const loaList = r.levelsOfAssurance || [];
              const claimsList = r.claims || [];
              const loaOk = !selLoa || loaList.length === 0 || loaList.some(v => selLoa.has(v));
              const claimsOk = !selClaims || claimsList.length === 0 || claimsList.some(v => selClaims.has(v));
              if (!(loaOk && claimsOk)) digitalIdExcluded.add(r.code.toUpperCase());
            });
          }
        }

        // Build set of country/state codes excluded by legislation tag filter
        const legTagExcluded = new Set();
        const selectedTags = window.__SELECTED_TAGS__;
        if (dataType === 'age_assurance') {
          const ageData = window.__AGE_ASSURANCE_TABLE_DATA__ || [];
          ageData.forEach(c => {
            if (c.hasRegionalLaws) {
              // For the parent country, only check its own (federal/EU-level) legislation
              const parentCounts = c.legislationTagCounts || {};
              if (!matchesSelectedLegislationTags(parentCounts, selectedTags)) {
                legTagExcluded.add(c.code.toUpperCase());
              }
              // For individual regions/states, check per-region tag counts
              const perRegion = c.perRegionLegislationTagCounts || {};
              Object.keys(perRegion).forEach(regionCode => {
                if (!matchesSelectedLegislationTags(perRegion[regionCode], selectedTags)) {
                  legTagExcluded.add(regionCode.toUpperCase());
                }
              });
              // Also exclude states that aren't in the per-region map at all
              if (c.code === 'US') {
                this.clickableStates.forEach(stateCode => {
                  if (!perRegion[stateCode] && !matchesSelectedLegislationTags({}, selectedTags)) {
                    legTagExcluded.add(stateCode);
                  }
                });
              }
            } else {
              const tagCounts = c.legislationTagCounts || {};
              if (!matchesSelectedLegislationTags(tagCounts, selectedTags)) {
                legTagExcluded.add(c.code.toUpperCase());
              }
            }
          });
        }

        // Update country borders: active if in the current mode's clickable set (and regulation level)
        if (this.meshes.countryBorders) {
          this.meshes.countryBorders.forEach(mesh => {
            // EU-only members: hide individual borders in EU/platform mode — the dissolved EU polygon
            // represents them. In national mode, show them individually (as inactive since they have
            // no own national law).
            if (dataType === 'age_assurance' && this.regulationLevel !== 'national' && this.euOnlyCountries.size > 0 && this.euOnlyCountries.has(mesh.userData.code)) {
              mesh.material.opacity = 0;
              mesh.material.needsUpdate = true;
              const fillMesh = mesh.userData.fillMesh;
              if (fillMesh && fillMesh.material) {
                fillMesh.material.opacity = 0;
                fillMesh.material.needsUpdate = true;
              }
              return;
            }

            const isClickable = countryClickableSet.has(mesh.userData.code) && !legTagExcluded.has(mesh.userData.code);
            const countryStatus = this.countryStatusMap.get(mesh.userData.code);
            const statusAllowed = !selectedStatuses || !countryStatus || selectedStatuses.has(countryStatus);
            let isActive;
            if (dataType === 'digital_ids') {
              isActive = isClickable && !digitalIdExcluded.has(mesh.userData.code);
              if (eudiFilterActive) isActive = isActive && eudiColorByCode.has(mesh.userData.code);
            } else if (this.regulationLevel === 'eu') {
              isActive = mesh.userData.code === 'EU' && !legTagExcluded.has('EU');
            } else if (this.regulationLevel === 'national') {
              // Hide the EU dissolved polygon; individual member nations take over
              if (mesh.userData.code === 'EU') {
                mesh.material.opacity = 0;
                mesh.material.needsUpdate = true;
                const fillMesh = mesh.userData.fillMesh;
                if (fillMesh && fillMesh.material) {
                  fillMesh.material.opacity = 0;
                  fillMesh.material.needsUpdate = true;
                }
                return;
              }
              isActive = isClickable && statusAllowed;
            } else if (this.regulationLevel === 'platform') {
              isActive = isClickable && statusAllowed;
            } else {
              isActive = false;
            }
            
            const eudiColor = isActive && eudiFilterActive ? eudiColorByCode.get(mesh.userData.code) : null;
            const color = isActive ? this.ACTIVE_BORDER_COLOR : this.INACTIVE_BORDER_COLOR;
            const activeFillColor = eudiColor != null ? eudiColor : this.ACTIVE_FILL_COLOR;
            const activeFillOpacity = eudiColor != null ? this.EUDI_FILL_OPACITY : this.ACTIVE_FILL_OPACITY;
            const renderOrder = isActive ? this.ACTIVE_RENDER_ORDER : this.INACTIVE_RENDER_ORDER;
            const style = this.getRegionInteractiveStyle(mesh);
            
            mesh.userData.originalColor = color;
            mesh.renderOrder = renderOrder;
            mesh.userData.originalOpacity = style.defaultOutlineOpacity;
            
            if (style.defaultOutlineOpacity === 0) {
              mesh.material.opacity = 0;
            } else {
              mesh.material.opacity = style.defaultOutlineOpacity;
              if (animate) {
                this.startTransition(mesh, color);
              } else {
                mesh.material.color.setHex(color);
                mesh.material.needsUpdate = true;
              }
            }
            // Default active fill: light blueish-purple for active, none for inactive
            if (style.useSubregionFills) {
              this.setSubregionFillsStyle(mesh.userData.code, this.subregionsActive ? this.ACTIVE_FILL_OPACITY : 0, this.subregionsActive ? this.ACTIVE_FILL_COLOR : null);
            } else {
              const fillMesh = mesh.userData.fillMesh;
              if (fillMesh && fillMesh.material) {
                if (isActive) {
                  fillMesh.material.color.setHex(activeFillColor);
                  fillMesh.material.opacity = activeFillOpacity;
                  fillMesh.userData.originalFillOpacity = activeFillOpacity;
                  fillMesh.userData.originalFillColor = activeFillColor;
                } else {
                  fillMesh.material.opacity = 0;
                  fillMesh.userData.originalFillOpacity = 0;
                  fillMesh.userData.originalFillColor = this.INACTIVE_BORDER_COLOR;
                }
                fillMesh.material.needsUpdate = true;
              }
            }
          });
        }
        
        // Update state borders: active only in Age Assurance mode when not National
        if (this.meshes.stateBorders) {
          this.meshes.stateBorders.forEach(mesh => {
            const isClickable = statesActive && this.clickableStates.has(mesh.userData.code) && !legTagExcluded.has(mesh.userData.code);
            const stateStatus = this.stateStatusMap.get(mesh.userData.code);
            const stateStatusAllowed = !selectedStatuses || !stateStatus || selectedStatuses.has(stateStatus);
            const isActive = isClickable && this.regulationLevel !== 'national' && this.regulationLevel !== 'eu' && stateStatusAllowed;
            
            const color = isActive ? this.ACTIVE_BORDER_COLOR : this.INACTIVE_BORDER_COLOR;
            const renderOrder = isActive ? this.ACTIVE_RENDER_ORDER : this.INACTIVE_RENDER_ORDER;
            
            mesh.userData.originalColor = color;
            mesh.renderOrder = renderOrder;
            
            if (animate) {
              this.startTransition(mesh, color);
            } else {
              mesh.material.color.setHex(color);
              mesh.material.needsUpdate = true;
            }
            // Default active fill for states
            const fillMesh = mesh.userData.fillMesh;
            if (fillMesh && fillMesh.material) {
              if (isActive) {
                fillMesh.material.color.setHex(this.ACTIVE_FILL_COLOR);
                fillMesh.material.opacity = this.ACTIVE_FILL_OPACITY;
                fillMesh.userData.originalFillOpacity = this.ACTIVE_FILL_OPACITY;
                fillMesh.userData.originalFillColor = this.ACTIVE_FILL_COLOR;
              } else {
                fillMesh.material.opacity = 0;
                fillMesh.userData.originalFillOpacity = 0;
                fillMesh.userData.originalFillColor = this.INACTIVE_BORDER_COLOR;
              }
              fillMesh.material.needsUpdate = true;
            }
          });
        }
      }

      // Start a smooth color transition for a mesh
,
}
